import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { dataAPI } from '../services/api';
import activityLog from '../services/activityLog';
import { Card, Chip, PillButton, IconCircle, ScoreBar, Modal, EmptyState, FeatureBanner } from '../ui';
import { FIELD_DEFS, CATEGORY_ICON, categoryOf } from '../utils/itemTypes';
import { ShareSheet } from './Share';
import SiteIcon from '../components/SiteIcon';
import EnvironmentView from '../components/EnvironmentView';
import SshKeyView from '../components/SshKeyView';
import { migrateLegacyData } from '../utils/sshKey';
import { ReactComponent as StarIcon } from '../assets/icons/star.svg';
import { ReactComponent as AlertTriangleIcon } from '../assets/icons/alert-triangle.svg';
import { ReactComponent as KeyIcon } from '../assets/icons/key-round.svg';
import { ReactComponent as EditIcon } from '../assets/icons/edit.svg';
import { ReactComponent as ShareIcon } from '../assets/icons/share.svg';
import { ReactComponent as TrashIcon } from '../assets/icons/trash.svg';
import { ReactComponent as ArrowLeftIcon } from '../assets/icons/arrow-left.svg';
import { ReactComponent as EyeIcon } from '../assets/icons/eye.svg';
import { ReactComponent as EyeOffIcon } from '../assets/icons/eye-off.svg';
import { ReactComponent as CopyIcon } from '../assets/icons/copy.svg';
import { ReactComponent as ExternalLinkIcon } from '../assets/icons/external-link.svg';
import { ReactComponent as DownloadIcon } from '../assets/icons/download.svg';
import { ReactComponent as ShieldCheckIcon } from '../assets/icons/shield-check.svg';
import { ReactComponent as FolderIcon } from '../assets/icons/tag.svg';
import { ReactComponent as InfoIcon } from '../assets/icons/info.svg';
import './ItemDetail.css';

// createdBy vale "extension" | "android" | "ios" (design/PASSKEYS.md).
const PASSKEY_ORIGIN_LABEL = {
  extension: 'itemDetail.passkey.createdByExtension',
  android: 'itemDetail.passkey.createdByAndroid',
  ios: 'itemDetail.passkey.createdByIos',
};

function ItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const rfidId = localStorage.getItem('rfidId');

  const [item, setItem] = useState(null); // { id, name, tags, data }
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [breach, setBreach] = useState({});
  const [showShare, setShowShare] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setNotFound(false);
    dataAPI.get(id, rfidId).then(async (res) => {
      const raw = res.data;
      const data = typeof raw?.data === 'string' ? JSON.parse(raw.data) : (raw?.data || {});
      // Una clave SSH con el formato antiguo (solo sshKey) se muestra en el nuevo (`view`); `data` sigue siendo lo guardado,
      // que es lo que se reenvia al marcar favorito.
      let view = data;
      if (categoryOf(data.type) === 'ssh') view = await migrateLegacyData(data).catch(() => data);
      setItem({ id: raw._id || id, name: raw.name, tags: raw.tags || [], data, view });
    }).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [id, rfidId]);

  useEffect(() => { load(); }, [load]);

  const category = useMemo(() => categoryOf(item?.data?.type), [item]);
  const fields = useMemo(() => (FIELD_DEFS[category] || FIELD_DEFS.note).filter((f) => f.key !== 'notes'), [category]);
  // Passkey no tiene `url`: el favicon sale de origin, o si no de rpId (ya es un dominio).
  const faviconSource = category === 'passkey' ? (item?.data?.origin || item?.data?.rpId) : item?.data?.url;

  // Avisos de Watchtower de este login (brecha, 2FA o passkeys disponibles), de la misma auditoria de Salud
  // (el servidor la cachea 10 min). Solo salen las comprobaciones que la cuenta tiene activas.
  const itemId = item?.id;
  const hasPassword = Boolean(item?.data?.password);
  const [watch, setWatch] = useState(null);
  useEffect(() => {
    if (!itemId || category !== 'login' || !hasPassword) { setWatch(null); return undefined; }
    let cancelled = false;
    api.get(`/security/health/${rfidId}`)
      .then((res) => { if (!cancelled) setWatch((res.data?.items || []).find((i) => String(i.id) === String(itemId)) || null); })
      .catch(() => { if (!cancelled) setWatch(null); });
    return () => { cancelled = true; };
  }, [itemId, category, hasPassword, rfidId]);

  // Registro local de actividad (developer.activityLog): solo lo que sale del item (nombre e id), nunca el valor.
  const logSecret = useCallback((kind, key) => {
    if (key === 'password') activityLog.record(kind, { itemId: item?.id, itemName: item?.name, origin: 'item' });
  }, [item]);

  const toggleReveal = (key) => {
    if (!revealed[key]) logSecret('reveal_password', key);
    setRevealed((p) => ({ ...p, [key]: !p[key] }));
  };

  const copy = useCallback(async (value, key) => {
    if (!value) return;
    await navigator.clipboard.writeText(String(value));
    logSecret('copy_password', key);
  }, [logSecret]);

  const toggleFavorite = useCallback(async () => {
    if (!item) return;
    const nextData = { ...item.data, favorite: !item.data.favorite };
    setItem((prev) => ({ ...prev, data: nextData })); // optimista
    try {
      await dataAPI.save({
        rfidId, itemId: item.id, name: item.name,
        description: item.name,
        data: nextData, tags: item.tags,
      });
    } catch {
      setItem((prev) => ({ ...prev, data: { ...prev.data, favorite: !nextData.favorite } })); // revierte
    }
  }, [item, rfidId]);

  const checkBreach = useCallback(async (key, value) => {
    if (!value) return;
    setBreach((b) => ({ ...b, [key]: { checking: true } }));
    try {
      const res = await api.post('/security/check-password', { password: value });
      setBreach((b) => ({ ...b, [key]: { ...res.data, checking: false } }));
    } catch {
      setBreach((b) => ({ ...b, [key]: { checking: false, error: true } }));
    }
  }, []);

  const remove = useCallback(async () => {
    setDeleting(true);
    try {
      await dataAPI.delete(item.id, rfidId);
      navigate('/');
    } catch {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  }, [item, rfidId, navigate]);

  const downloadFile = useCallback(async () => {
    const res = await dataAPI.download(item.id, rfidId);
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = item.data.fileName || item.name;
    a.click();
    URL.revokeObjectURL(url);
  }, [item, rfidId]);

  if (loading) return <p className="sgc-item-detail__loading">{t('common.loading', 'Cargando…')}</p>;
  if (notFound || !item) {
    return <EmptyState icon={FolderIcon} title={t('itemDetail.notFound')} actionLabel={t('itemDetail.back')} onAction={() => navigate('/')} />;
  }

  const Icon = CATEGORY_ICON[category] || FolderIcon;

  return (
    <div className="sgc-item-detail">
      <div className="sgc-item-detail__header">
        <button type="button" className="sgc-item-detail__back" onClick={() => navigate(-1)} title={t('itemDetail.back')}>
          <ArrowLeftIcon width={20} height={20} aria-hidden="true" />
        </button>
        <SiteIcon
          url={faviconSource}
          className="sgc-item-detail__favicon"
          fallback={(
            <span className="sgc-item-detail__favicon-fallback">
              <IconCircle icon={Icon} size={44} />
            </span>
          )}
        />
        <div className="sgc-item-detail__title-block">
          <h1 className="sgc-item-detail__title">{item.name}</h1>
          <div className="sgc-item-detail__chips">
            <Chip label={t(`dashboard.categories.${category}`, category)} />
            {item.data.vault && <Chip label={item.data.vault} />}
            {(item.tags || []).map((tag) => <Chip key={tag} label={tag} />)}
          </div>
        </div>
        <div className="sgc-item-detail__actions">
          <button type="button" onClick={toggleFavorite} title={t('itemDetail.favorite')} className={item.data.favorite ? 'sgc-item-detail__action--active' : ''}>
            <StarIcon width={18} height={18} aria-hidden="true" />
          </button>
          <button type="button" onClick={() => navigate(`/vault/${id}/edit`)} title={t('itemDetail.edit')}>
            <EditIcon width={18} height={18} aria-hidden="true" />
          </button>
          <button type="button" onClick={() => setShowShare(true)} title={t('itemDetail.share')}>
            <ShareIcon width={18} height={18} aria-hidden="true" />
          </button>
          <button type="button" onClick={() => setShowDeleteConfirm(true)} title={t('itemDetail.delete')}>
            <TrashIcon width={18} height={18} aria-hidden="true" />
          </button>
        </div>
      </div>

      {category === 'passkey' && (
        <FeatureBanner
          icon={InfoIcon}
          tone="surface"
          title={t('itemDetail.passkey.bannerTitle')}
          subtitle={t('itemDetail.passkey.bannerSubtitle')}
        />
      )}

      {watch && (watch.breached || watch.twoFactorAvailable || watch.passkeysAvailable) && (
        <div className="sgc-item-detail__alerts">
          {watch.breached && (
            <div className="sgc-item-detail__alert sgc-item-detail__alert--danger" role="alert">
              <AlertTriangleIcon width={18} height={18} aria-hidden="true" />
              <p className="sgc-item-detail__alert-text">{t('healthPage.breachDetail', { name: watch.breachName, date: watch.breachDate })}</p>
            </div>
          )}
          {watch.twoFactorAvailable && (
            <div className="sgc-item-detail__alert sgc-item-detail__alert--accent" role="status">
              <ShieldCheckIcon width={18} height={18} aria-hidden="true" />
              <p className="sgc-item-detail__alert-text">{t('itemDetail.watchtower.twoFactor')}</p>
              {watch.twoFactorDocUrl && (
                <a className="sgc-item-detail__alert-doc" href={watch.twoFactorDocUrl} target="_blank" rel="noopener noreferrer">
                  {t('healthPage.docLink')}<ExternalLinkIcon width={14} height={14} aria-hidden="true" />
                </a>
              )}
            </div>
          )}
          {watch.passkeysAvailable && (
            <div className="sgc-item-detail__alert sgc-item-detail__alert--accent" role="status">
              <KeyIcon width={18} height={18} aria-hidden="true" />
              <p className="sgc-item-detail__alert-text">{t('itemDetail.watchtower.passkeys')}</p>
              {watch.passkeysDocUrl && (
                <a className="sgc-item-detail__alert-doc" href={watch.passkeysDocUrl} target="_blank" rel="noopener noreferrer">
                  {t('healthPage.docLink')}<ExternalLinkIcon width={14} height={14} aria-hidden="true" />
                </a>
              )}
            </div>
          )}
        </div>
      )}

      {category === 'environment' && <EnvironmentView item={item} />}

      {category === 'ssh' && <SshKeyView item={item} data={item.view || item.data} />}

      {category !== 'environment' && category !== 'ssh' && (
      <Card className="sgc-item-detail__fields">
        {category === 'passkey' ? (
          <>
            {[
              ['rpId', item.data.rpId],
              ['rpName', item.data.rpName],
              ['userName', item.data.userName],
              ['userDisplayName', item.data.userDisplayName],
              ['createdBy', item.data.createdBy ? t(PASSKEY_ORIGIN_LABEL[item.data.createdBy] || '', item.data.createdBy) : ''],
              ['createdAt', item.data.createdAt ? new Date(item.data.createdAt).toLocaleString() : ''],
              ['lastUsedAt', item.data.lastUsedAt ? new Date(item.data.lastUsedAt).toLocaleString() : t('itemDetail.passkey.neverUsed')],
            ].map(([key, value]) => (value ? (
              <div key={key} className="sgc-item-detail__field">
                <span className="sgc-item-detail__field-label">{t(`itemDetail.passkey.${key}`)}</span>
                <div className="sgc-item-detail__field-value">
                  <span className="sgc-item-detail__field-text">{value}</span>
                </div>
              </div>
            ) : null))}
          </>
        ) : category === 'file' ? (
          <div className="sgc-item-detail__field">
            <span className="sgc-item-detail__field-label">{t('dataEntry.file')}</span>
            <div className="sgc-item-detail__field-value">
              <span>{item.data.fileName || '—'}</span>
              <button type="button" onClick={downloadFile} title={t('itemDetail.download')}>
                <DownloadIcon width={16} height={16} aria-hidden="true" />
              </button>
            </div>
          </div>
        ) : (
          fields.map((f) => {
            const value = item.data[f.key];
            if (value === undefined || value === '') return null;
            const b = breach[f.key];
            return (
              <div key={f.key} className="sgc-item-detail__field">
                <span className="sgc-item-detail__field-label">{t(f.labelKey)}</span>
                <div className="sgc-item-detail__field-value">
                  <span className="sgc-item-detail__field-text">
                    {f.secret ? (revealed[f.key] ? value : '••••••••') : String(value)}
                  </span>
                  {f.type === 'url' && (
                    <button type="button" onClick={() => window.open(value, '_blank', 'noopener')} title={t('itemDetail.openUrl')}>
                      <ExternalLinkIcon width={16} height={16} aria-hidden="true" />
                    </button>
                  )}
                  {f.secret && (
                    <button type="button" onClick={() => toggleReveal(f.key)} title={t('dashboard.reveal')}>
                      {revealed[f.key] ? <EyeOffIcon width={16} height={16} aria-hidden="true" /> : <EyeIcon width={16} height={16} aria-hidden="true" />}
                    </button>
                  )}
                  <button type="button" onClick={() => copy(value, f.key)} title={t('dashboard.copy')}>
                    <CopyIcon width={16} height={16} aria-hidden="true" />
                  </button>
                  {f.key === 'password' && (
                    <button type="button" onClick={() => checkBreach(f.key, value)} title={t('itemDetail.checkBreach')}>
                      <ShieldCheckIcon width={16} height={16} aria-hidden="true" />
                    </button>
                  )}
                </div>
                {f.key === 'password' && b && (
                  <div className="sgc-item-detail__breach">
                    {b.checking ? (
                      <span className="sgc-item-detail__muted">{t('itemDetail.checking')}</span>
                    ) : b.error ? null : (
                      <>
                        {typeof b.score === 'number' && <ScoreBar value={b.score} />}
                        <span className={b.pwned ? 'sgc-item-detail__breach--found' : 'sgc-item-detail__muted'}>
                          {b.pwned ? t('itemDetail.breachFound', { count: b.count }) : t('itemDetail.breachClean')}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </Card>
      )}

      {item.data.notes && (
        <Card>
          <h2 className="sgc-item-detail__notes-title">{t('itemDetail.notes')}</h2>
          <p className="sgc-item-detail__notes">{item.data.notes}</p>
        </Card>
      )}

      <ShareSheet
        open={showShare}
        onClose={() => setShowShare(false)}
        initialItem={{ id: item.id, title: item.name }}
      />

      <Modal open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} title={t('itemDetail.deleteTitle')}>
        <p>{t('itemDetail.confirmDelete', { name: item.name })}</p>
        <div className="sgc-item-detail__confirm-actions">
          <PillButton variant="secondary" onClick={() => setShowDeleteConfirm(false)}>{t('common.cancel', 'Cancelar')}</PillButton>
          <PillButton variant="danger" loading={deleting} onClick={remove}>{t('itemDetail.delete')}</PillButton>
        </div>
      </Modal>
    </div>
  );
}

export default ItemDetail;
